<template>
  <el-dialog
    v-if="isShow"
    :title="typeof address.id !== 'undefined' ? 'Edit Address' : 'Create Address'"
    :visible.sync="isShow"
    width="80%"
    top="5vh"
    :fullscreen="isFullscreen"
  >
    <el-form
      ref="addressForm"
      v-loading="isSaving"
      :rules="rules"
      :model="address"
    >
      <el-row>
        <el-col :md="24">
          <el-row :gutter="10">
            <el-col :md="12">
              <el-form-item
                label="Name"
                prop="name"
              >
                <el-input
                  v-model="address.name"
                  placeholder="Name (e.g. Company)"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="12">
              <el-form-item
                label="Code"
                prop="code"
              >
                <el-input
                  v-model="address.code"
                  placeholder="Code"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :md="12">
              <el-form-item
                label="Line One"
                prop="line_one"
              >
                <el-input
                  v-model="address.line_one"
                  placeholder="Line One (e.g. Unit 10)"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="12">
              <el-form-item
                label="Line Two"
                prop="line_two"
              >
                <el-input
                  v-model="address.line_two"
                  placeholder="Line Two (e.g. 1 Main Road)"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :md="8">
              <el-form-item
                label="Suburb"
                prop="suburb"
              >
                <el-autocomplete
                  v-model="address.suburb"
                  :fetch-suggestions="suburbQuerySearch"
                  placeholder="Enter name of suburb"
                  :trigger-on-focus="false"
                  class="w-100"
                  placement="top"
                  :debounce="500"
                  clearable
                  @select="handleSelectSuburb"
                >
                  <template v-slot="{ item }">
                    {{ formatSuburbByLocation(item) }}
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>
            <el-col :md="8">
              <el-form-item
                label="State"
                prop="state"
              >
                <el-input
                  v-model="address.state"
                  placeholder="E.g. Victoria"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="8">
              <el-form-item
                label="Postcode"
                prop="postcode"
              >
                <el-input
                  v-model="address.postcode"
                  placeholder="E.g. 3000"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :md="8">
              <el-form-item
                label="Contact"
                prop="contact"
              >
                <el-input
                  v-model="address.contact"
                  placeholder="E.g. John Smith"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="8">
              <el-form-item
                label="Phone"
                prop="phone"
              >
                <el-input
                  v-model="address.phone"
                  placeholder="E.g. 03 9700 5555"
                  clearable
                />
              </el-form-item>
            </el-col>
            <el-col :md="8">
              <el-form-item
                label="Email"
                prop="email"
                :rules="[{ type: 'email', message: 'Please input correct email address', trigger: ['blur', 'change'] }]"
              >
                <el-input
                  v-model="address.email"
                  placeholder="E.g. john.smith@company.com"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <hr>
          <el-row>
            <el-col :md="24">
              <el-form-item
                label="RTP Account"
                prop="rtp_account"
              >
                <el-input
                  v-model="address.rtp_account"
                  placeholder="RTP Account"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :md="24">
              <el-form-item
                label="Special Instructions"
                prop="spi"
              >
                <el-input
                  v-model="address.spi"
                  placeholder="Please enter any special instructions"
                  type="textarea"
                  :rows="4"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
    </el-form>
    <span
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click.native="isFullscreen = !isFullscreen">
        <FontAwesomeIcon
          v-if="isFullscreen"
          :icon="['fal', 'compress']"
        />
        <FontAwesomeIcon
          v-else
          :icon="['fal', 'expand']"
        />
      </el-button>
      <el-button @click.native="isShow = false">
        Cancel
      </el-button>
      <el-button
        type="primary"
        :loading="isSaving"
        @click.native="typeof address.id !== 'undefined' ? updateAddress() : createAddress()"
      >
        {{ typeof address.id !== 'undefined' ? 'Save' : 'Create' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  props: {
    accountId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      address: {},
      isShow: false,
      isSaving: false,
      isFullscreen: false,
      rules: {
        name: [
          { required: true, message: 'Name of the address is required', trigger: 'blur' }
        ],
        line_one: [
          { required: true, message: 'Line one of the address is required', trigger: 'blur' }
        ],
        suburb: [
          { required: true, message: 'Please enter and select a suburb from the list', trigger: 'change' }
        ],
        state: [
          { required: true, message: 'State of the address is required', trigger: 'change' }
        ],
        postcode: [
          { required: true, message: 'Postcode of the address is required', trigger: 'change' }
        ]
      }
    }
  },
  methods: {
    createAddress () {
      this.$refs.addressForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.$sdk.account_address.create(this.address, builder => {
            builder.context = { resource: 'account', id: this.accountId }
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.isShow = false
              this.$emit('saved')
            })
        }
      })
    },
    updateAddress () {
      this.$refs.addressForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.address.update(builder => {
            builder.context = { resource: 'account', id: this.accountId }
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.isShow = false
              this.$emit('saved')
            })
        }
      })
    },
    suburbQuerySearch (queryString, cb) {
      this.$sdk.dictionary_location.list(builder => {
        builder.page = 1
        builder.limit = 50
        builder.filters = { dictionary_location_find: queryString }
        builder.order = 'suburb:asc'
      }).then(res => {
        cb(res)
      })
    },
    handleSelectSuburb (item) {
      this.address.suburb = item.suburb
      this.address.state = item.state
      this.address.postcode = item.postcode
    },
    formatSuburbByLocation (location) {
      if (location) {
        return `${location.suburb}, ${location.state}, ${location.postcode}`
      } else return ''
    }
  }
}
</script>

<style>

</style>
